import React, { useEffect, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import formatDate, { formatDOBDate } from '../../../utils/format-date';
import Breadcrumb from '../../../utils/breadcrumbs';
import { FiUser, FiMail, FiPhone, FiCalendar, FiCheckCircle, FiXCircle, FiClock, FiMapPin, FiSlash, FiShield } from 'react-icons/fi';
import Loader from '../../../utils/loader';
import EditUserOffcanvas from '../../../components/users/edit-offcanvas';
import { useLocation } from 'react-router-dom';
import api from '../../../libs/axios';

enum UserStatus {
    PENDING = 'pending',
    VERIFIED = 'verified',
    SUSPENDED = 'suspended',
    INITIATED = 'initiated',
    // DEACTIVATED = 'deactivated'
}

interface UserDetailsAttributes {
    first_name?: string;
    last_name?: string;
    email?: string;
    date_of_birth?: Date;
    gender?: string;
    profile_picture?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
    created_at?: Date;
    updated_at?: Date;
}

interface User {
    id: string;
    role: string;
    role_id: number | string;
    phone_number: string;
    status: UserStatus;
    created_at?: Date;
    updated_at?: Date;
    details?: UserDetailsAttributes;
}

const ViewUser: React.FC = () => {
    const location = useLocation();
    const userId = location.state?.userId;
    const [loading, setLoading] = useState<boolean>(false);
    const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
    const [user, setUser] = useState<User | null>(null);

    const fetchUser = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/users/${userId}`);
            setUser(response.data.user);
        } catch (error: any) {
            console.log("Error fetching user data:", error.response.data);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (userId) {
            fetchUser();
        }
    }, [userId]);

    const getStatusBadge = (status: UserStatus) => {
        switch (status) {
            case UserStatus.PENDING:
                return 'bg-yellow-100 text-yellow-800';
            case UserStatus.VERIFIED:
                return 'bg-green-100 text-green-800';
            case UserStatus.SUSPENDED:
                return 'bg-red-100 text-red-800';
            // case UserStatus.DEACTIVATED:
            //     return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getFullName = () => {
        const firstName = user?.details?.first_name || '';
        const lastName = user?.details?.last_name || '';
        return `${firstName} ${lastName}`.trim() || 'Unknown User';
    };

    return (
        <div className="w-full min-h-screen bg-white p-4 md:p-6">
            <div className="mx-auto">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                    <h1 className="text-xl font-bold text-gray-800 flex items-center mb-4 md:mb-0">
                        <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                        User Profile
                    </h1>
                    <Breadcrumb />
                </div>

                {loading ? (
                    <Loader />
                ) : (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 space-y-6 relative">
                        <button
                            onClick={() => setIsEditOpen(true)}
                            className="absolute top-6 right-6 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center transition-colors duration-200"
                        >
                            <FaEdit className="mr-2" />
                            Edit
                        </button>

                        {/* Header section with basic details */}
                        <div className="flex flex-col md:flex-row md:items-center gap-6">
                            <div className="flex-shrink-0">
                                <div className="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                                    {user?.details?.profile_picture ? (
                                        <img
                                            src={user?.details.profile_picture}
                                            alt="Profile"
                                            className="w-28 h-28 rounded-full object-cover"
                                        />
                                    ) : (
                                        <FiUser className="w-12 h-12 text-gray-400" />
                                    )}
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center flex-wrap gap-2">
                                    <h1 className="text-2xl font-bold text-gray-800">
                                        {getFullName()}
                                    </h1>
                                    <div className="flex gap-2 flex-wrap">
                                        <span className={`px-3 py-1 ${getStatusBadge(user?.status as UserStatus)} text-xs capitalize rounded-full flex items-center`}>
                                            {user?.status}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-3 text-gray-600 flex flex-col gap-1">
                                    {user?.details?.email && (
                                        <div className="flex items-center">
                                            <FiMail className="mr-2" />
                                            <span>{user?.details.email}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center">
                                        <FiPhone className="mr-2" />
                                        <span>{user?.phone_number}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <FiShield className="mr-2" />
                                        Role:<span className="bg-yellow-100 ml-1.5 px-3 py-1 text-xs capitalize rounded-full flex items-center text-yellow-800">{user?.role}</span>
                                    </div>
                                    {user?.details?.date_of_birth && (
                                        <div className="flex items-center">
                                            <FiCalendar className="mr-2" />
                                            <span>Born: {formatDOBDate(user?.details?.date_of_birth.toString())}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Main info grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Personal Information */}
                            <div className="space-y-4">
                                <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 flex items-center">
                                    <FiUser className="mr-2" /> Personal Information
                                </h2>
                                <div className="space-y-3">
                                    <div className="flex flex-col">
                                        <span className="font-medium text-gray-700">Full Name:</span>
                                        <span className="text-gray-600">{getFullName()}</span>
                                    </div>

                                    {user?.details?.email && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">Email:</span>
                                            <span className="text-gray-600">{user?.details.email}</span>
                                        </div>
                                    )}

                                    <div className="flex flex-col">
                                        <span className="font-medium text-gray-700">Phone Number:</span>
                                        <span className="text-gray-600">{user?.phone_number}</span>
                                    </div>

                                    {user?.details?.gender && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">Gender:</span>
                                            <span className="text-gray-600">{user?.details.gender.charAt(0).toUpperCase() + user?.details.gender.slice(1)}</span>
                                        </div>
                                    )}

                                    {user?.details?.date_of_birth && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">Date of Birth:</span>
                                            <span className="text-gray-600">{formatDOBDate(user?.details.date_of_birth.toString())}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Account & Address Information */}
                            <div className="space-y-4">
                                <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 flex items-center">
                                    <FiMapPin className="mr-2" /> Account & Address Information
                                </h2>
                                <div className="space-y-3">

                                    {user?.details?.address && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">Address:</span>
                                            <span className="text-gray-600">{user?.details.address}</span>
                                        </div>
                                    )}

                                    {user?.details?.city && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">City:</span>
                                            <span className="text-gray-600">{user?.details.city}</span>
                                        </div>
                                    )}

                                    {user?.details?.state && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">State:</span>
                                            <span className="text-gray-600">{user?.details.state}</span>
                                        </div>
                                    )}

                                    {user?.details?.country && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">Country:</span>
                                            <span className="text-gray-600">{user?.details.country}</span>
                                        </div>
                                    )}

                                    {user?.details?.pincode && (
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-700">Pincode:</span>
                                            <span className="text-gray-600">{user?.details.pincode}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Timestamps */}
                        <div className="pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                                {user?.created_at && (
                                    <div>
                                        <span className="font-medium">Account Created On:</span>
                                        <span className="ml-2">{formatDate(user?.created_at.toString())}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit offcanvas */}
                {isEditOpen && <EditUserOffcanvas
                    showEditUserCanvas={isEditOpen}
                    setShowEditUserCanvas={setIsEditOpen}
                    fetchUser={fetchUser}
                    user={user} />}
            </div>
        </div>
    );
};

export default ViewUser;